import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Image from "react-bootstrap/Image";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { Container, Nav, NavItem } from "react-bootstrap";
import { fetchUserPermission } from "../../components/SecurityComponent/SecurityComponent";
import headerLogo from "../../../src/assets/images/honest-health-header.png";
import { loginRequest, tokenRequest } from "../../authConfig";
import logMessage from "../../../src/utils/logger";
import { LOG_MESSAGE } from "../../config/app.constant";
import { fetchData, patchData } from "../../api/apiService";
import { BRANDING_EMAIL_DOMAIN } from "../../config/app.constant";
import moment from "moment";

const Header = () => {
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const { instance, accounts } = useMsal();
  const [accessToken, setAccessToken] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(null);
  const popoverRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutClicked, setLogoutClicked] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [showSessionTimeoutBanner, setShowSessionTimeoutBanner] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const tokenExpOverrideSec = useRef(null);
  const STORAGE_LAST_ACTIVITY = "lastActivityAt";
  const STORAGE_SESSION_EXPIRY_WARNING_SHOWN = "sessionExpiryWarningShown";
  const STORAGE_SESSION_ACTION = "sessionSessionAction";
  const AUTH_EVENT_KEY = "auth:event";

  const safeStorageGet = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  const safeStorageSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  };

  const safeStorageRemove = (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  };
  // for setting banner after inactivity logout 
  const setCookie = (name, value, maxAgeSeconds = 120) => {
    try {
      document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
      return true;
    } catch (e) {
      return false;
    }
  };

  const getCookie = (name) => {
    try {
      const cookies = document.cookie ? document.cookie.split("; ") : [];
      for (const c of cookies) {
        const [k, ...rest] = c.split("=");
        if (k === name) return decodeURIComponent(rest.join("=") || "");
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const deleteCookie = (name) => {
    try {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
      return true;
    } catch (e) {
      return false;
    }
  };

  const getAndClearCookie = (name) => {
    const v = getCookie(name);
    if (v != null) deleteCookie(name);
    return v;
  };


  const getAndClearUrlParam = (paramName) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const value = params.get(paramName);
      if (!value) return null;

      params.delete(paramName);
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "") +
        window.location.hash;

      window.history.replaceState({}, document.title, newUrl);
      return value;
    } catch (e) {
      return null;
    }
  };


  const tableauRef = useRef(null);
  const lastActivityTime = useRef(Date.now());
  const [sessionTimeout, setSessionTimeout] = useState(0);
  const [sessionTimeoutWarning, setSessionTimeoutWarning] = useState(60);
  const sessionExpiryWarned = useRef(false);
  const [showSessionExpiryModal, setShowSessionExpiryModal] = useState(false);
  const [sessionExpiryRemaining, setSessionExpiryRemaining] = useState(null);
  const [isSessionExtending, setIsSessionExtending] = useState(false);

  
  useEffect(() => {
    const reason = safeStorageGet("logoutReason");
    const cookieReason = getAndClearCookie("ch_logout_reason");
    const urlReason = getAndClearUrlParam("logoutReason");

    if (
      reason === "INACTIVITY" ||
      cookieReason === "INACTIVITY" ||
      urlReason === "INACTIVITY"
    ) {
      setShowSessionTimeoutBanner(true);
      safeStorageRemove("logoutReason");
    }
  }, []);
useEffect(() => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      tokenRequest.account = account;

      instance
        .acquireTokenSilent(tokenRequest)
        .then((response) => {
          if (response !== null) {
            logMessage("info", LOG_MESSAGE.USER_LOGGED_IN, {
              userData: response,
            });
            setAccessToken(response.accessToken);
          }
        })
        .catch(() => {});
    } else {
      localStorage.clear();
    }
  }, [accounts, instance]);

  useEffect(() => {
    if (accessToken && !localStorage.getItem("accessToken")) {
      if (
        localStorage.getItem("msal.account.keys") &&
        localStorage.getItem("msal.account.keys") !== "[]"
      ) {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("user_error_count", 0);
        if (location.pathname === "/") {
          navigate("/agreement");
        }
      } else {
        localStorage.clear();
        instance.logoutRedirect({ postLogoutRedirectUri: "/" });
      }
    }
    if (accessToken) {
      const fetchData = async () => {
        try {
          const userPermissions = await fetchUserPermission();
          if (userPermissions.dev_security_label.length === 0) {
            setPermissions(["nav_logout"]);
          } else {
            setPermissions(userPermissions.dev_security_label);
          }
          setPermissionsLoaded(true);
          setUserLoggedIn(true);
          if (!safeStorageGet(STORAGE_LAST_ACTIVITY)) {
            safeStorageSet(STORAGE_LAST_ACTIVITY, Date.now().toString());
          }
        } catch (error) {
          if (error.statusCode === 401) {
            setUserLoggedIn(false);
          }
          if (error.statusCode === 403) {
            setPermissions(["nav_logout"]);
            navigate("/account-locked");
          }
        }
      };

      fetchData();
    }
  }, [accessToken, location.pathname, navigate]);

  useEffect(() => {
    const fetchDataAndInitializeCounter = async () => {
      try {
        const appConfigurationResponse = await fetchData(
          "/application-configuration?key=session_timeout&key=session_timeout_warning",
        );
        const appConfiguration =
          appConfigurationResponse.application_configurations.find(
            (item) => item.name === "session_timeout",
          );

        if (appConfiguration) {
          const sessionTimeoutInMinutes = parseInt(appConfiguration.value, 10);
          const sessionTimeoutInSeconds = sessionTimeoutInMinutes * 60;
          setSessionTimeout(sessionTimeoutInSeconds);

          const warningConfiguration =
            appConfigurationResponse.application_configurations.find(
              (item) => item.name === "session_timeout_warning",
            );

          if (warningConfiguration) {
            const warningInSeconds = parseInt(warningConfiguration.value, 10);
            setSessionTimeoutWarning(
              Number.isFinite(warningInSeconds) ? warningInSeconds * 60 : 30,
            );
          }
        }
      } catch (error) {
      }
    };

    if (userLoggedIn) {
      fetchDataAndInitializeCounter();
    }
    return () => {};
  }, [userLoggedIn]);

  const broadcastLogout = () => {
    try {
      safeStorageSet(
        AUTH_EVENT_KEY,
        JSON.stringify({ type: "LOGOUT", at: Date.now() }),
      );
    } catch (e) {}
  };


  const broadcastSessionExtend = () => {
    try {
      safeStorageSet(
        AUTH_EVENT_KEY,
        JSON.stringify({ type: "EXTEND_SESSION", at: Date.now() }),
      );
    } catch (e) {}
  };

  const performLocalLogout = () => {
    const _logoutReason = safeStorageGet("logoutReason");
    setShowSessionExpiryModal(false);
    setSessionExpiryRemaining(null);
    safeStorageRemove(STORAGE_SESSION_EXPIRY_WARNING_SHOWN);
    safeStorageRemove(STORAGE_SESSION_ACTION);

    // For banner - temp cookie
    if (_logoutReason === "INACTIVITY") {
      setCookie("ch_logout_reason", "INACTIVITY", 180);
    }

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}

    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
      account: instance.getActiveAccount(),
    });
  };

  const handleLogout = useCallback(async () => {
    logMessage("info", LOG_MESSAGE.USER_LOGGED_OUT, {});
    setShowSessionExpiryModal(false);
    setSessionExpiryRemaining(null);
    safeStorageSet(
      STORAGE_SESSION_ACTION,
      JSON.stringify({ type: "logout", at: Date.now() }),
    );
    setLogoutClicked(true);
    broadcastLogout();
    const user = localStorage.getItem("user_id");
    try {
      await patchData(`/users/${user}/clearPVPReview`, {});
      await patchData(`/users/${user}/clearDocumentReview`, {});
      performLocalLogout();
      broadcastLogout();
    } catch (error) {
      performLocalLogout();
      broadcastLogout();
    }
  }, [instance]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== AUTH_EVENT_KEY || !e.newValue) return;

      try {
        const msg = JSON.parse(e.newValue);
        if (msg?.type === "LOGOUT") {
          if (logoutClicked) return;
          setShowSessionExpiryModal(false);
          setLogoutClicked(true);
          performLocalLogout();
        } else if (msg?.type === "EXTEND_SESSION") {
          setShowSessionExpiryModal(false);
          setSessionExpiryRemaining(null);
          resetTimeout();
        }
      } catch (err) {}
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [logoutClicked, instance]);
  
  useEffect(() => {
    if (!userLoggedIn || sessionTimeout <= 0) return;

        const intervalId = setInterval(() => {
      if (logoutClicked) return;
      const lastActivity =
        Number(safeStorageGet(STORAGE_LAST_ACTIVITY)) || lastActivityTime.current;

      const elapsed = (Date.now() - lastActivity) / 1000;
      const remaining = Math.max(0, Math.ceil(sessionTimeout - elapsed));

      let sessionActionType = null;
      try {
        const raw = safeStorageGet(STORAGE_SESSION_ACTION);
        if (raw) {
          const parsed = JSON.parse(raw);
          sessionActionType = parsed?.type || null;
        }
      } catch (e) {}

      const warningShown = safeStorageGet(STORAGE_SESSION_EXPIRY_WARNING_SHOWN) === "true";
      if (sessionActionType === "logout") return;

      if (
        sessionTimeoutWarning > 0 &&
        remaining > 0 &&
        remaining <= sessionTimeoutWarning
      ) {
        setSessionExpiryRemaining(remaining);

        if (!warningShown) {
          safeStorageSet(STORAGE_SESSION_EXPIRY_WARNING_SHOWN, "true");
          setShowSessionExpiryModal(true);
        } else if (
          !showSessionExpiryModal &&
          sessionActionType !== "cancel" &&
          sessionActionType !== "logout"
        ) {
          setShowSessionExpiryModal(true);
        }
      } else if (!showSessionExpiryModal) {
        setSessionExpiryRemaining(null);
      }

      if (elapsed >= sessionTimeout) {
        safeStorageSet("logoutReason", "INACTIVITY");
        handleLogout();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [
    sessionTimeout,
    sessionTimeoutWarning,
    userLoggedIn,
    handleLogout,
    showSessionExpiryModal,
    logoutClicked,
  ]);
useEffect(() => {
    const fetchMaintenanceData = async () => {
      try {
        const data = await fetchData("/maintenance/nextMaintenance");

        if (data?.statusCode === 404) {
          return;
        }

        if (data) {
          const start = moment(data.startDateTime);
          const end = moment(data.endDateTime);

          const sameDay = start.isSame(end, "day");
          let maintenanceMessage = null;

          if (sameDay) {
            const startHour = start.hour();
            const endHour = end.hour();

            if (startHour < 12 && endHour < 12) {

              maintenanceMessage = (
                <>
                  CareHub will be undergoing maintenance on{" "}
                  <strong>{start.format("MM/DD/YY")}</strong> from{" "}
                  <strong>
                    {start.format("h:mm")} - {end.format("h:mm a")}
                  </strong>{" "}
                  CST.
                </>
              );
            } else {

              maintenanceMessage = (
                <>
                  CareHub will be undergoing maintenance on{" "}
                  <strong>{start.format("MM/DD/YY")}</strong> from{" "}
                  <strong>
                    {start.format("h:mm a")} - {end.format("h:mm a")}
                  </strong>{" "}
                  CST.
                </>
              );
            }
          } else {
            maintenanceMessage = (
              <>
                CareHub will be undergoing maintenance on{" "}
                <strong>{start.format("MM/DD/YY h:mm a")}</strong> -{" "}
                <strong>{end.format("MM/DD/YY h:mm a")}</strong> CST.
              </>
            );
          }

          setMaintenanceMessage(maintenanceMessage);
        }
      } catch (error) {
        setMaintenanceMessage("");
}
    };

    fetchMaintenanceData();
  }, [location.pathname]);

  const resetTimeout = () => {
    const now = Date.now();
    lastActivityTime.current = now;
    safeStorageSet(STORAGE_LAST_ACTIVITY, now.toString());
    safeStorageRemove(STORAGE_SESSION_EXPIRY_WARNING_SHOWN);
    safeStorageRemove(STORAGE_SESSION_ACTION);
    sessionExpiryWarned.current = false;
  };

  const handleSessionExtend = async () => {
    sessionExpiryWarned.current = false;
    setShowSessionExpiryModal(false);
    setIsSessionExtending(true);

    try {
      if (accounts && accounts.length > 0) {
        tokenRequest.account = accounts[0];

        const res = await instance.acquireTokenSilent({
          ...tokenRequest,
          account: accounts[0],
          forceRefresh: true,
        });

        if (res?.accessToken) {
          setAccessToken(res.accessToken);

          localStorage.setItem("awstoken", res.accessToken);

          if (process.env.NODE_ENV === "development") {
            const TEST_TOKEN_EXPIRE_IN_SECONDS = 0; // to avoid eslint error
            tokenExpOverrideSec.current =
              Math.floor(Date.now() / 1000) + TEST_TOKEN_EXPIRE_IN_SECONDS;
          }
        }
      }
    } catch (e) {}

    setIsSessionExtending(false);
    resetTimeout();
    broadcastSessionExtend();
  };


  const handleSessionLogoutNow = () => {
    setShowSessionExpiryModal(false);
    safeStorageSet(
      STORAGE_SESSION_ACTION,
      JSON.stringify({ type: "logout", at: Date.now() }),
    );
    handleLogout();
  };

  const loginUser = () => {
    instance.loginRedirect(loginRequest);
  };

  
  useEffect(() => {
    const handleActivity = () => {};

    const handleIframeMessage = (event) => {};

    const setupTableauEventListeners = () => {
      const tableauViz = tableauRef.current;

      if (tableauViz) {
        tableauViz.addEventListener("marksselection", handleActivity);
        tableauViz.addEventListener("filterchange", handleActivity);
        tableauViz.addEventListener("parametervaluechange", handleActivity);
        tableauViz.addEventListener("customviewload", handleActivity);
        tableauViz.addEventListener("customviewsave", handleActivity);
        tableauViz.addEventListener("tabchange", handleActivity);
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("message", handleIframeMessage);

    setupTableauEventListeners();

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("message", handleIframeMessage);

      const tableauViz = tableauRef.current;
      if (tableauViz) {
        tableauViz.removeEventListener("marksselection", handleActivity);
        tableauViz.removeEventListener("filterchange", handleActivity);
        tableauViz.removeEventListener("parametervaluechange", handleActivity);
        tableauViz.removeEventListener("customviewload", handleActivity);
        tableauViz.removeEventListener("customviewsave", handleActivity);
        tableauViz.removeEventListener("tabchange", handleActivity);
      }
    };
  }, []);
const popover = (
    <Popover
      id="popover-secondary"
      className="mt-2"
      style={{ maxWidth: "none" }}
      ref={popoverRef}
    >
      <Popover.Body>
        <div style={{ width: "300px" }}>
          <p>
            <strong>Name:</strong> {localStorage.getItem("user_name")}
          </p>
          <p>
            <strong>Email:</strong> {localStorage.getItem("user_email")}
          </p>
        </div>
      </Popover.Body>
    </Popover>
  );

  if (userLoggedIn === false) {
    if (
      location.pathname !== "/" &&
      location.pathname !== "/contact" &&
      location.pathname !== "/health"
    ) {
      return <Navigate to="/" replace />;
    }
  }

  if (!permissionsLoaded) {
    return (
      <>
        <Nav className="navbar navbar-expand-lg navbar-light shadow-sm sticky-top">
          <Container className="container px-3 px-sm-0">
            <Link to="/" className="navbar-brand text-white">
              <Image src={headerLogo} className="header-logo" />
            </Link>
            <Button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarSupportedContent"
              aria-controls="navbarSupportedContent"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </Button>
            <Container
              className="collapse navbar-collapse"
              id="navbarSupportedContent"
            >
              <Nav className="navbar-nav ms-auto mb-2 mb-lg-0">
                <Nav.Item className="nav-item">
                  <Link
                    to={`${BRANDING_EMAIL_DOMAIN}/news-insights/`}
                    target="_blank"
                    className="nav-link nav-inactive underline hide"
                  >
                    News
                  </Link>
                </Nav.Item>
                <Nav.Item className="nav-item">
                  <Link
                    to="/contact"
                    className={
                      location.pathname === "/contact"
                        ? "nav-link nav-active underline hide"
                        : "nav-link nav-inactive underline hide"
                    }
                  >
                    Contact
                  </Link>
                </Nav.Item>
                <UnauthenticatedTemplate>
                  <Nav.Item className="nav-item">
                    <Link
                      className="nav-link nav-inactive underline"
                      onClick={loginUser}
                    >
                      Login
                    </Link>
                  </Nav.Item>
                </UnauthenticatedTemplate>
                <AuthenticatedTemplate>
                  {permissions?.includes("nav_carehub") && (
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom"
                      overlay={popover}
                    >
                      <NavItem className="nav-item">
                        <OverlayTrigger
                          trigger="click"
                          placement="bottom"
                          overlay={popover}
                        >
                          <Link className="nav-link nav-inactive underline">
                            Profile
                          </Link>
                        </OverlayTrigger>
                      </NavItem>
                    </OverlayTrigger>
                  )}
                  {(permissions?.includes("nav_carehub") ||
                    permissions?.includes("nav_logout")) && (
                    <Link
                      onClick={logoutClicked ? null : handleLogout}
                      className={`nav-link  ${
                        logoutClicked ? "nav-inactive " : ""
                      }`}
                      disabled={logoutClicked}
                    >
                      {logoutClicked ? "Logging Out..." : "Logout"}
                    </Link>
                  )}
                </AuthenticatedTemplate>
              </Nav>
            </Container>
          </Container>
        </Nav>

        {showSessionTimeoutBanner && (
          <div
            style={{
              padding: "8px 16px",
              backgroundColor: "#FFF3CD",
              color: "#000",
              position: "relative",
              textAlign: "center",
            }}
          >
            <span>
              For your security, your session has timed out due to inactivity.
              Please log in again to continue.
            </span>

            <span
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => setShowSessionTimeoutBanner(false)}
            >
              ✕
            </span>
          </div>
        )}


      </>
    );
  }

  return (
    <>
      <Nav className="navbar navbar-expand-lg navbar-light shadow-sm sticky-top">
        <Container className="container px-3 px-sm-0">
          {permissions?.includes("home_insight") ? (
            <Link to="/insights" className="navbar-brand text-white">
              <Image src={headerLogo} className="header-logo" />
            </Link>
          ) : permissions?.includes("nav_pvp") ? (
            <Link to="/pvp" className="navbar-brand text-white">
              <Image src={headerLogo} className="header-logo" />
            </Link>
          ) : (
            <Link to="/" className="navbar-brand text-white">
              <Image src={headerLogo} className="header-logo" />
            </Link>
          )}

          <Button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </Button>
          <Container
            className="collapse navbar-collapse"
            id="navbarSupportedContent"
          >
            <Nav className="navbar-nav ms-auto mb-2 mb-lg-0">
              {permissions?.includes("nav_insights") && (
                <Nav.Item className="nav-item underline">
                  <Link
                    to="/insights"
                    className={
                      location.pathname.includes("insight")
                        ? "nav-link nav-active"
                        : "nav-link nav-inactive"
                    }
                  >
                    Insights
                  </Link>
                </Nav.Item>
              )}
              {permissions?.includes("nav_administration") && (
                <Nav.Item className="nav-item ">
                  <Link
                    to="/administration"
                    className={
                      location.pathname === "/administration" ||
                      location.pathname === "/application_configuration" ||
                      location.pathname === "/user_management" ||
                      location.pathname === "/request_details"
                        ? "nav-link nav-active underline"
                        : "nav-link nav-inactive underline"
                    }
                  >
                    Administration
                  </Link>
                </Nav.Item>
              )}
              {permissions?.includes("nav_role_management") && (
                <Nav.Item className="nav-item">
                  <Link
                    to="/role_management"
                    className={
                      location.pathname === "/role_management"
                        ? "nav-link nav-active underline"
                        : "nav-link nav-inactive underline"
                    }
                  >
                    Role Management
                  </Link>
                </Nav.Item>
              )}
              {permissions?.includes("nav_pvp") && (
                <Nav.Item className="nav-item underline">
                  <Link
                    to="/pvp"
                    className={
                      location.pathname.includes("pvp")
                        ? "nav-link nav-active"
                        : "nav-link nav-inactive"
                    }
                  >
                    PVP
                  </Link>
                </Nav.Item>
              )}
              <Nav.Item className="nav-item">
                <Link
                  to={`${BRANDING_EMAIL_DOMAIN}/news-insights/`}
                  target="_blank"
                  className="nav-link nav-inactive  underline hide"
                >
                  News
                </Link>
              </Nav.Item>
              <Nav.Item className="nav-item">
                <Link
                  to="/contact"
                  className={
                    location.pathname === "/contact"
                      ? "nav-link nav-active underline hide"
                      : "nav-link nav-inactive underline hide"
                  }
                >
                  Contact
                </Link>
              </Nav.Item>
              {permissions?.includes("nav_carehub") && (
                <NavItem className="nav-item">
                  <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    overlay={popover}
                  >
                    <Link className="nav-link nav-inactive underline">
                      Profile
                    </Link>
                  </OverlayTrigger>
                </NavItem>
              )}
              {(permissions?.includes("nav_carehub") ||
                permissions?.includes("nav_logout")) && (
                <NavItem className="nav-item">
                  <Link
                    onClick={logoutClicked ? null : handleLogout}
                    className={`nav-link  ${
                      logoutClicked ? "nav-inactive" : "underline"
                    }`}
                    disabled={logoutClicked}
                  >
                    {logoutClicked ? "Logging Out..." : "Logout"}
                  </Link>
                </NavItem>
              )}
            </Nav>
          </Container>
        </Container>
      </Nav>
      {maintenanceMessage && showBanner && (
        <div
          style={{
            backgroundColor: "#FFF3CD",
            color: "#000",
            textAlign: "center",
            padding: "5px 0",
            fontSize: "14px",
            position: "relative",
          }}
        >
          {maintenanceMessage}
          <span
            onClick={() => setShowBanner(false)}
            style={{
              position: "absolute",
              right: "10px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            ✕
          </span>
        </div>
      )}

      <Modal
        show={showSessionExpiryModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title>Session Expiry Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Number.isFinite(Number(sessionExpiryRemaining)) ? (
            <>Your session is about to expire in {sessionExpiryRemaining} seconds.</>
          ) : (
            <>Your session is about to expire.</>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSessionExtend}
            disabled={isSessionExtending}
          >
            Keep me Logged In
          </Button>
          <Button variant="danger" onClick={handleSessionLogoutNow}>
            Log me Out
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Header;
